import { customLog } from 'helpers/customLog';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  PurchaseJourneyContent,
  getPurchaseJourneyContent,
} from '@/content/purchase-journey-content';
import { ctService, sentryService } from '@/services/index';

export const normalizeQuerySegment = (value: string | null): string | null =>
  value?.replace(/\s+/g, '-') ?? null;

export const getBundlePlanData = (
  content: PurchaseJourneyContent,
  packageSelection: string | null
) =>
  packageSelection
    ? ((content as any).bundlePlanSelection?.offerCode?.[packageSelection] ?? null)
    : null;

export const useQueryParams = () => {
  const { search, pathname } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const isPaymentRedirect = queryParams.get('callback');
  const packageSelection = queryParams.get('offerCode');
  const content = getPurchaseJourneyContent();

  const bundlePlanData = useMemo(
    () => getBundlePlanData(content, packageSelection),
    [content, packageSelection]
  );

  const skus = bundlePlanData?.productSkus ?? null;
  const category = normalizeQuerySegment(bundlePlanData?.category ?? null);
  const deviceSkus = bundlePlanData?.device?.sku ?? null;
  const subCategory = normalizeQuerySegment(bundlePlanData?.subCategory ?? null);
  const refId = queryParams.get('RefId');

  const hasProcessedProductInitialQuery = useRef(false);
  const couponCode = null;
  const [kkProducts, setKKProducts] = useState<any[]>([]);

  const fetchKKProducts = useCallback(() => {
    if (hasProcessedProductInitialQuery.current) return;

    hasProcessedProductInitialQuery.current = true;
    const serviceCall = ctService.getAllProduct(true);

    serviceCall
      .then(productList => {
        setKKProducts(productList?.length ? productList : []);
        customLog('ctService getAllProduct', productList);
      })
      .catch(err => {
        sentryService.captureException(err);
      });
  }, []);

  useEffect(() => {
    if (!skus) return;

    customLog('query param calling');
    fetchKKProducts();
  }, [fetchKKProducts, skus]);

  return {
    kkProducts,
    skus,
    couponCode,
    category,
    refId,
    pathname,
    searchParams: search,
    isPaymentRedirect,
    subCategory,
    deviceSkus,
  };
};
